import { api, RouterInput } from "../../../services/rpc/RpcProvider";
import { useCallback } from "react";
import { useToast } from "@introist/react-foundation/v2";

export const useDatasetSyncConfig = (datasetId: string) => {
  const { data: config } = api.datasets.sync.config.find.useQuery({ datasetId });

  return { config };
};

export const useDatasetSyncConfigOptions = (datasourceId: string | null) => {
  const { data: options, isFetching } = api.datasets.sync.config.options.useQuery(
    { datasourceId: datasourceId ?? "" },
    { enabled: !!datasourceId }
  );

  return { options, loading: isFetching };
};

export type DatasetSyncConfigCreate = RouterInput["datasets"]["sync"]["config"]["create"];
export type DatasetSyncConfigUpdate =
  RouterInput["datasets"]["sync"]["config"]["update"]["updates"];

export const useDatasetSyncActions = () => {
  const toast = useToast();

  const createOp = api.datasets.sync.config.create.useMutation({});
  const updateOp = api.datasets.sync.config.update.useMutation({});
  const deleteOp = api.datasets.sync.config.delete.useMutation({});

  const startOp = api.datasets.sync.start.useMutation({});

  const apiUtils = api.useContext();

  const findOp = apiUtils.datasets.sync.config.find;
  const logOp = apiUtils.datasets.sync.logs;

  const create = useCallback(
    async (params: DatasetSyncConfigCreate) => {
      try {
        const res = await createOp.mutateAsync(params);
        await findOp.refetch({ datasetId: params.datasetId });
        toast.success("Sync created");
        return res;
      } catch (e) {
        toast.error("Failed to create sync");
        throw e;
      }
    },
    [createOp, findOp, toast]
  );

  const update = useCallback(
    async (id: string, updates: DatasetSyncConfigUpdate) => {
      try {
        const res = await updateOp.mutateAsync({ id, updates });
        await findOp.refetch();
        return res;
      } catch (e) {
        toast.error("Failed to update sync");
        throw e;
      }
    },
    [updateOp, findOp, toast]
  );

  const start = useCallback(
    async (datasetId: string) => {
      await startOp
        .mutateAsync({ datasetId })
        .then(() => {
          logOp.refetch({ datasetId });
          toast.success("Sync started");
        })
        .catch(() => {
          toast.error("Failed to start sync");
        });
    },
    [startOp, logOp, toast]
  );

  const remove = useCallback(
    async (id: string) => {
      await deleteOp
        .mutateAsync({ id })
        .then(async () => {
          await findOp.refetch();
          toast.success("Sync deleted");
        })
        .catch(e => {
          toast.error("Failed to delete sync");
          throw e;
        });
    },
    [deleteOp, findOp, toast]
  );

  return { create, update, start, remove };
};
